// src/pages/ScriptureSearchPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { callFunction } from "@/integrations/supabase/functions";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, ExternalLink } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { searchPassages } from "@/components/ai-prayer/companion/lib/abielApi";
import { 
  buildThematicSearchQuery, 
  penalizeOverusedVerses, 
  sortVersesByRelevance 
} from "@/utils/verseSelection";

/**
 * This page no longer shows a search box.
 * It IMMEDIATELY loads "related verses" based on:
 *   - ?query=            (e.g., "John 3:16")
 *   - location.state.seedText  (the VOTD text we sent along)
 *
 * It calls your edge function "related-verses" and expects:
 *   { items: Array<{ reference: string; text: string; score?: number }> }
 *
 * If your function name or response shape differs, tweak the fetchRelated() method.
 */

type RelatedItem = { reference: string; text: string; score?: number };

export default function ScriptureSearchPage() {
  const location = useLocation() as { state?: { seedText?: string } };
  const [params] = useSearchParams();

  const queryRef = useMemo(() => params.get("query") || "", [params]);
  const seedText = location?.state?.seedText || (window as any).__ABIEL_SEARCH_SEED__?.text || "";

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<RelatedItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [usedFallback, setUsedFallback] = useState(false);

  useEffect(() => {
    const fetchRelated = async () => {
      if (!queryRef && !seedText) {
        setItems([]);
        return;
      }
      setLoading(true);
      setError(null);
      setUsedFallback(false);
      try {
        // Try the edge function first
        const { data, error: fnError } = await callFunction("related-verses", {
          method: "POST",
          body: {
            reference: queryRef,
            text: seedText,
            limit: 12,
          },
        });

        if (fnError) throw fnError;

        // Expected shape: { items: [{ reference, text, score? }, ...] }
        let result = (data?.items ?? []) as RelatedItem[];
        
        // If empty, use AI-assisted fallback
        if (result.length === 0) {
          console.log('[ScriptureSearchPage] No DB results, trying AI-assisted search');
          const query = buildThematicSearchQuery(queryRef + " " + seedText, ['study', 'context']);
          const verses = await searchPassages(query, 10);
          
          // Re-rank and de-duplicate
          const candidates = verses.map((v: any) => ({
            reference: v.reference || '',
            text: v.text || v.content || '',
            score: v.score || 0.5
          }));
          
          const penalized = penalizeOverusedVerses(candidates, seedText);
          const sorted = sortVersesByRelevance(penalized);
          
          result = sorted.slice(0, 12);
          setUsedFallback(true);
        }

        setItems(result);
        if (!result.length) {
          toast("No related verses found", {
            description: "Try another passage or a broader reference.",
          });
        }
      } catch (err: any) {
        console.error("[ScriptureSearchPage] related-verses failed:", err);
        setError("Failed to load related verses");
        toast.error("Could not load related verses.");
      } finally {
        setLoading(false);
      }
    };

    fetchRelated();
  }, [queryRef, seedText]);

  return (
    <div className="mx-auto w-full max-w-2xl p-4">
      <h1 className="text-xl font-semibold mb-3">Related Verses</h1>
      {queryRef && (
        <p className="text-xs text-muted-foreground mb-2">
          Seed reference: <span className="font-medium">{queryRef}</span>
        </p>
      )}
      {seedText && (
        <p className="text-xs text-muted-foreground mb-4">
          Seed text: "{seedText}"
        </p>
      )}
      
      {usedFallback && items.length > 0 && (
        <p className="text-xs text-muted-foreground mb-4 italic">
          Showing AI-assisted results
        </p>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading related verses…
        </div>
      )}

      {error && (
        <Card className="border-destructive/40" role="alert" aria-describedby="error-description">
          <CardContent className="p-4">
            <p id="error-description" className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      {!loading && !error && items.length === 0 && (
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">
              No related verses yet.
            </p>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {items.map((it, idx) => (
          <Card key={`${it.reference}-${idx}`}>
            <CardContent className="p-4 space-y-1">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium">{it.reference}</div>
                {/* Optional: open your Bible reader if you have a handler/route */}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    const handlers = (window as any).__ABIEL_HANDLERS__;
                    const routes = (window as any).__ABIEL_ROUTES__;
                    if (handlers?.openBibleRead) return handlers.openBibleRead(it.reference);
                    if (routes?.bibleRead) {
                      window.location.href = `${routes.bibleRead}?ref=${encodeURIComponent(it.reference)}`;
                    }
                  }}
                  className="h-8 px-2"
                >
                  Open <ExternalLink className="ml-1 h-3 w-3" />
                </Button>
              </div>
              <p className="text-base leading-relaxed">{it.text}</p>
              {typeof it.score === "number" && (
                <p className="text-[11px] text-muted-foreground">relevance {Math.round(it.score * 100)}%</p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
